import { put as apiPut, get as apiGet } from './apiService';
import { UserCourse } from '../types/userCourse';

// Request queue to serialize all getUserCourse and updateUserCourse calls
const requestQueue: Promise<unknown> = Promise.resolve();
let queueTail = requestQueue;

/**
 * Serializes async operations to ensure they execute one at a time
 * @param fn - The async function to execute
 * @returns Promise that resolves when the function completes
 */
async function serialize<T>(fn: () => Promise<T>): Promise<T> {
  const myTurn = queueTail;
  let resolve: (value: unknown) => void;
  queueTail = new Promise((r) => {
    resolve = r;
  });

  try {
    await myTurn;
    return await fn();
  } finally {
    resolve!(undefined);
  }
}

/**
 * Update UserCourse (including Lessons progress)
 * @param userCourse - The UserCourse object to update
 * @returns Promise with updated UserCourse
 */
export async function updateUserCourse(userCourse: UserCourse): Promise<UserCourse> {
  return serialize(async () => {
    const { certificationId, courseId, profileId } = userCourse;

    // Validate required fields before making API call
    if (!certificationId) {
      throw new Error('Cannot update user course: certificationId is required');
    }
    if (!courseId) {
      throw new Error('Cannot update user course: courseId is required');
    }
    if (!profileId) {
      throw new Error('Cannot update user course: profileId is required');
    }

    const updatedUserCourse = await apiPut<UserCourse>(
      `/certification/${certificationId}/course/${courseId}/user/${profileId}`,
      userCourse
    );

    return updatedUserCourse;
  });
}

/**
 * Get UserCourse by certification, course, and profile IDs
 * @param certificationId - The certification ID
 * @param courseId - The course ID
 * @param profileId - The profile ID
 * @returns Promise with UserCourse or null if not found
 */
export async function getUserCourse(
  certificationId: string,
  courseId: string,
  profileId: string
): Promise<UserCourse | null> {
  return serialize(async () => {
    const userCourse = await apiGet<UserCourse>(
      `/certification/${certificationId}/course/${courseId}/user/${profileId}?expand=lessonResponses`
    );

    // Verify that the API response includes certificationId
    if (userCourse && !userCourse.certificationId) {
      // If the API doesn't return certificationId, add it from the request parameters
      userCourse.certificationId = certificationId;
    }
    if (userCourse && !userCourse.courseId) {
      userCourse.courseId = courseId;
    }
    if (userCourse && !userCourse.profileId) {
      userCourse.profileId = profileId;
    }

    return userCourse;
  });
}

// Convenience export
export const userCourseService = {
  updateUserCourse,
  getUserCourse,
};
